import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as uarActions from 'store/uar/actions';
import { selectCartItems, selectActionForApp } from 'store/uar/selectors';

/**
 * Uses redux state to manage the UAR action cart, providing methods to add, remove, and clear items, 
 * as well as retrieve selected actions for specific staff members and apps.
 */
const usePersistentUarActionCart = () => {
	const dispatch = useDispatch();
	const sCartItems = useSelector(selectCartItems);

	const addOrToggleAction = useCallback(
		item => {
			if (item?.itemKey && item?.action) {
				dispatch(uarActions.addCartAction(item));
			}
		},
		[dispatch]
	);

	const removeCartItem = useCallback(
		itemKey => {
			dispatch(uarActions.removeCartAction(itemKey));
		},
		[dispatch]
	);

	const clearCart = useCallback(() => {
		dispatch(uarActions.clearActionCart());
	}, [dispatch]);

	const getSelectedAction = useCallback(
		(staffMemberId, appId) => {
			return selectActionForApp(
				{ uar: { actionCart: { items: sCartItems } } },
				staffMemberId,
				appId
			);
		},
		[sCartItems]
	);

	const cartCount = useMemo(() => sCartItems.length, [sCartItems]);
	const hasItems = useMemo(() => cartCount > 0, [cartCount]);

	return {
		sCartItems,
		addOrToggleAction,
		clearCart,
		removeCartItem,
		getSelectedAction,
		hasItems,
		cartCount,
	};
};

export default usePersistentUarActionCart;
